/**
 * **Edit a punch** — `docs/visit-page.md` §11, §4.9; Board 7 B (popup 440,
 * opened from any punch on the Time tab).
 *
 * *Punch* and *Time*, then **Reason** — required for any change, and *Save*
 * stays grey until there is one — and **THIS PUNCH**, the punch's history.
 * Saving is `PATCH /punches/:intervalId {startedAt | endedAt, reason}`: a
 * `punch_events` correction through the crew app's own `correctInterval`, so
 * the original stays in History and the person is told.
 *
 * *Punch*'s choices are the person's punches that day, in Board 18 E's drawn
 * words (*Clocked in · Arrived · Lunch from · Left · Clocked out*) and Board
 * 7 B's own *End lunch* (lead round 1, item 6).
 */
import { useMemo, useState } from 'react'
import type { EditPunchInput, PunchEvent, PunchHistoryEntry, PunchKind, TimeDayCard, TimePersonRow, VisitRead } from '@rowted/shared'
import { ADD_TIME, EDIT_PUNCH } from './copy.js'
import { dayText, timeIn } from './format.js'
import { Face } from './face.js'
import { VisitButton, VisitPopup, VISIT_LAYER, VISIT_LAYER_OVER } from './sheet.js'
import { ChoiceField, clockOf, FieldLabel, instantAt, minuteOfDay, parseClock, TimeBox } from './time-money-parts.js'

/** What each punch is called in *Punch*. */
export const PUNCH_WORDS: Record<PunchKind, string> = {
  clock_in: ADD_TIME.clockedIn,
  arrived: ADD_TIME.arrived,
  lunch_start: ADD_TIME.lunchFrom,
  lunch_end: EDIT_PUNCH.kind.lunch_end,
  left: ADD_TIME.left,
  clock_out: ADD_TIME.clockedOut,
}

export function EditPunchPopup({ read, card, row, punch, phone, onClose, onSave }: {
  read: VisitRead
  card: TimeDayCard
  row: TimePersonRow
  punch: PunchEvent
  phone: boolean
  onClose: () => void
  onSave: (intervalId: string, body: EditPunchInput) => Promise<void>
}) {
  const [kind, setKind] = useState<PunchKind>(punch.kind)
  const chosen = row.punches.find((candidate) => candidate.kind === kind) ?? punch
  const [time, setTime] = useState(() => clockOf(minuteOfDay(read.timeZone, punch.at)))
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const minute = parseClock(time)
  const ready = reason.trim().length > 0 && minute !== null && !busy

  const choices = useMemo(() => row.punches.map((candidate) => ({ value: candidate.kind, label: PUNCH_WORDS[candidate.kind] })), [row])

  function pick(next: PunchKind) {
    setKind(next)
    const found = row.punches.find((candidate) => candidate.kind === next)
    if (found) setTime(clockOf(minuteOfDay(read.timeZone, found.at)))
  }

  async function save() {
    if (!ready || minute === null) return
    setBusy(true)
    setProblem(null)
    const at = instantAt(read.timeZone, card.day.date, minute)
    try {
      await onSave(chosen.target.intervalId, {
        ...(chosen.target.edge === 'start' ? { startedAt: at } : { endedAt: at }),
        reason: reason.trim(),
        visitId: read.id,
      })
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : String(cause))
      setBusy(false)
    }
  }

  return (
    <VisitPopup
      width={440}
      phone={phone}
      title={EDIT_PUNCH.title(row.person.name)}
      sub={EDIT_PUNCH.sub(dayText(card.day.date, 'date'), String(card.day.index), read.reference)}
      mark={<Face person={row.person} size={34} />}
      onClose={onClose}
      note={EDIT_PUNCH.original}
      layer={VISIT_LAYER}
      asDrawn="vp-07 B"
      footer={(
        <>
          <VisitButton size={phone ? 'phone' : 'popup'} onClick={onClose}>{EDIT_PUNCH.cancel}</VisitButton>
          <VisitButton size={phone ? 'phone' : 'popup'} tone="primary" disabled={!ready} onClick={() => void save()}>
            {EDIT_PUNCH.save}
          </VisitButton>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <div className={phone ? 'flex flex-col gap-3' : 'grid grid-cols-2 gap-x-3.5 gap-y-3'}>
          <ChoiceField
            label={EDIT_PUNCH.punch}
            value={kind}
            choices={choices}
            onChange={pick}
            phone={phone}
            layer={VISIT_LAYER_OVER}
          />
          <div className="min-w-0">
            <FieldLabel htmlFor="edit-punch-time">{EDIT_PUNCH.time}</FieldLabel>
            <TimeBox id="edit-punch-time" label={EDIT_PUNCH.time} value={time} onChange={setTime} height={36} autoFocus phone={phone} />
          </div>
        </div>

        <div className="min-w-0">
          <FieldLabel htmlFor="edit-punch-reason" required>{EDIT_PUNCH.reason}</FieldLabel>
          <textarea
            id="edit-punch-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className="block h-16 w-full resize-none rounded-button tabular-nums border border-line bg-surface px-3 py-2.5 text-[13px]
                       leading-normal text-text outline-none focus:border-accent focus:shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]"
          />
          <p className="m-0 mt-1 text-[11px] leading-[1.4] text-text-faint">{EDIT_PUNCH.reasonHelp(row.person.firstName)}</p>
        </div>

        {chosen.history.length > 0 && (
          <div className="mt-1 flex flex-col gap-[7px] border-t border-line-faint pt-2.5 text-[12px] text-text-body">
            <span className="text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">{EDIT_PUNCH.thisPunch}</span>
            {chosen.history.map((entry, index) => (
              <HistoryLine key={`${entry.kind}-${index}`} entry={entry} timeZone={read.timeZone} />
            ))}
          </div>
        )}

        {problem && <p className="m-0 text-[12.5px] text-danger">{problem}</p>}
      </div>
    </VisitPopup>
  )
}

/**
 * One line of **THIS PUNCH** — *Marco Diaz added it on the crew iPad · Wed
 * 4:58 PM — “forgot to tap back in”*; *Jesse Lutz was told and didn’t dispute
 * it*. A kind Board 7 B does not draw names the person, when and why in the
 * drawn line's own punctuation (a DEVIATION REQUEST for its words).
 */
function HistoryLine({ entry, timeZone }: { entry: PunchHistoryEntry; timeZone: string }) {
  const when = entry.at ? timeIn(timeZone, entry.at, 'dayAmpm') : null
  let words: string
  if (entry.kind === 'added' && entry.via === 'crew_ipad' && when) {
    words = EDIT_PUNCH.addedOnIpad(when, entry.reason ?? '')
  } else if (entry.kind === 'told' && entry.disputed === false) {
    words = EDIT_PUNCH.toldNoDispute
  } else {
    words = [when, entry.reason ? `“${entry.reason}”` : null].filter(Boolean).join(' — ')
  }
  return (
    <div className="flex items-start gap-2">
      <Face person={entry.by} size={20} />
      <span><b>{entry.by.name}</b> {words}</span>
    </div>
  )
}
